import React from 'react'
import { Link } from 'react-router-dom'
import clsx from 'clsx'

export interface FooterProps {
  className?: string
  variant?: 'marketing' | 'minimal'
}

// Marketing footer for public pages
const MarketingFooter: React.FC<{ className?: string }> = ({ className }) => {
  const currentYear = new Date().getFullYear()

  const footerLinks = {
    Product: [
      { label: 'Home', href: '/' },
      { label: 'World Cup Blitz', href: '/wc' },
      { label: 'Leaderboard', href: '/wc/leaderboard' },
    ],
    Legal: [
      { label: 'Privacy Policy', href: '/privacy' },
      { label: 'Terms of Service', href: '/terms' },
      { label: 'Official Rules', href: '/official-rules' },
      { label: 'Privacy Choices', href: '/privacy-choices' },
      { label: 'Accessibility', href: '/accessibility' },
    ],
    Support: [
      { label: 'Help Center', href: '/help' },
      { label: 'Contact Us', href: '/contact' },
      { label: 'Privacy Choices', href: '/privacy-choices' },
      { label: 'Status', href: 'https://status.bracketblitz.app', external: true },
    ],
  }

  return (
    <footer className={clsx('bg-dark-600 border-t border-dark-500', className)}>
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-12">
        {/* Main footer content */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-8">
          {Object.entries(footerLinks).map(([category, links]) => (
            <div key={category}>
              <h3 className="text-sm font-semibold text-text-primary mb-4">
                {category}
              </h3>
              <ul className="space-y-3">
                {links.map((link) => (
                  <li key={link.href}>
                    {(link as { external?: boolean }).external ? (
                      <a
                        href={link.href}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-sm text-text-secondary hover:text-primary-400 transition-colors"
                      >
                        {link.label}
                      </a>
                    ) : (
                      <Link
                        to={link.href}
                        className="text-sm text-text-secondary hover:text-primary-400 transition-colors"
                      >
                        {link.label}
                      </Link>
                    )}
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>

        {/* Bottom section */}
        <div className="pt-8 border-t border-dark-500 text-center">
          <div className="flex items-center justify-center space-x-3 mb-4">
            <div className="h-8 w-8 bg-gradient-to-r from-primary-500 to-accent-500 rounded-lg flex items-center justify-center">
              <span className="text-white font-heading font-bold text-lg">B</span>
            </div>
            <span className="text-lg font-heading font-bold text-gradient-fire">
              BracketBlitz
            </span>
          </div>
          
          <p className="text-xs text-text-muted">
            © {currentYear} BracketBlitz. Independent fan games and brackets.
          </p>
        </div>
      </div>
    </footer>
  )
}

// Minimal footer for authenticated pages (if needed)
const MinimalFooter: React.FC<{ className?: string }> = ({ className }) => {
  const currentYear = new Date().getFullYear()

  return (
    <footer className={clsx('bg-dark-700 border-t border-dark-500 py-4', className)}>
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between text-xs text-text-muted">
          <div className="flex flex-wrap items-center gap-x-4 gap-y-2">
            <Link to="/help" className="hover:text-primary-400 transition-colors">
              Help
            </Link>
            <Link to="/privacy" className="hover:text-primary-400 transition-colors">
              Privacy
            </Link>
            <Link to="/terms" className="hover:text-primary-400 transition-colors">
              Terms
            </Link>
            <Link to="/official-rules" className="hover:text-primary-400 transition-colors">
              Rules
            </Link>
            <Link to="/privacy-choices" className="hover:text-primary-400 transition-colors">
              Choices
            </Link>
            <Link to="/accessibility" className="hover:text-primary-400 transition-colors">
              Accessibility
            </Link>
            <Link to="/contact" className="hover:text-primary-400 transition-colors">
              Contact
            </Link>
            <a 
              href="https://status.bracketblitz.app" 
              target="_blank" 
              rel="noopener noreferrer"
              className="hover:text-primary-400 transition-colors"
            >
              Status
            </a>
          </div>
          <p className="mt-2 sm:mt-0">
            © {currentYear} BracketBlitz
          </p>
        </div>
      </div>
    </footer>
  )
}

const Footer: React.FC<FooterProps> = ({ className, variant = 'marketing' }) => {
  if (variant === 'minimal') {
    return <MinimalFooter className={className} />
  }

  return <MarketingFooter className={className} />
}

export { Footer, MinimalFooter }
